import { classRoomRegistration } from "lib/common/endpoints/endUser";
import { getTrialInfo } from "lib/common/endpoints/public";
import { Classroom } from "lib/common/types/classroom";
import useTrialRequest from "lib/hooks/useTrialRequest";
import { useRouter } from "next/router";
import React, { useEffect, useRef } from "react";
import { Sparkle, Clock, CheckCircle, XCircle } from "@phosphor-icons/react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  getUser,
  isLoggedIn,
  getAuthChecked,
  getOnBoardingStatus,
} from "redux/reducers/userSlice";
import useSWR from "swr";
import Button from "../atomic/Button";
import {
  BillIcon,
  DocumentIcon,
  ImageIcon,
  PageIcon,
  WalletIcon,
} from "../Icon";
import EsewaPayment from "../Payments/Esewa";
import KhaltiPayment from "../Payments/Khalti";
import Steps from "../Steps";
import Step from "../Steps/step";
import RegisterDownloadReciept from "./RegisterDownloadReciept";
import RegisterForm from "./RegisterForm";
import RegisterOther from "./PaymentMethods";
import RegisterStep from "./RegisterStep";
import RegisterUploadImage from "./RegisterUploadImage";
import PaymentMethods from "./PaymentMethods";
import RegisterUserImage from "./RegisterUserImage";
import { showSignInDrawer } from "redux/reducers/drawerSlice";
import Link from "next/link";

function daysLeft(endsAt?: number | null) {
  if (!endsAt) return 0;
  return Math.max(0, Math.ceil((endsAt - Date.now() / 1000) / 86400));
}

/**
 * Trials are requested, not auto-granted -- a request just goes to an
 * admin for approval/denial (see useTrialRequest / xylene/admin_panel/
 * trial/routes.py ApproveTrialRequest/DenyTrialRequest). Decoupled from
 * registration entirely: this renders regardless of registration status.
 */
function TrialNotice({
  classroomId,
  trialDurationDays,
  trialEnabled = true,
}: {
  classroomId: string;
  trialDurationDays?: number;
  trialEnabled?: boolean;
}) {
  const { trial, requestTrial, loading, error } = useTrialRequest(classroomId);

  if (trial.status === "active") {
    const remaining = daysLeft(trial.endsAt);
    return (
      <div className="flex items-center gap-2 rounded-xl border border-primary-100 dark:border-primary-900/40 bg-primary-50 dark:bg-primary-900/20 px-4 py-3 mb-4">
        <Sparkle size={16} weight="fill" className="text-primary dark:text-primary-300 shrink-0" />
        <p className="text-sm text-gray-700 dark:text-gray-300">
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            Free trial active
          </span>{" "}
          — {remaining} day{remaining === 1 ? "" : "s"} left of full access while your
          payment is verified.
        </p>
      </div>
    );
  }

  if (trial.status === "pending") {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 mb-4">
        <Clock size={16} className="text-amber-600 dark:text-amber-400 shrink-0" />
        <p className="text-sm text-amber-800 dark:text-amber-300">
          Your free trial request is pending admin approval.
        </p>
      </div>
    );
  }

  if (trial.status === "denied") {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 mb-4">
        <XCircle size={16} className="text-gray-400 dark:text-gray-500 shrink-0" />
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Your free trial request for this course was not approved.
        </p>
      </div>
    );
  }

  if (trial.status === "expired") {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 mb-4">
        <Clock size={16} className="text-gray-400 dark:text-gray-500 shrink-0" />
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Your free trial has ended. Full access unlocks once your payment is
          verified.
        </p>
      </div>
    );
  }

  if (trial.status === "converted") {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-green-100 dark:border-green-900/40 bg-green-50 dark:bg-green-900/20 px-4 py-3 mb-4">
        <CheckCircle size={16} weight="fill" className="text-green-600 dark:text-green-400 shrink-0" />
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Your payment was verified before your trial ended — you have full,
          permanent access.
        </p>
      </div>
    );
  }

  // No trial requested yet -- offer to ask for one, unless this course has
  // opted out of trials entirely (classroom.trialEnabled === false).
  if (!trialDurationDays || !trialEnabled) return null;
  return (
    <div className="rounded-xl border border-primary-100 dark:border-primary-900/40 bg-primary-50 dark:bg-primary-900/20 px-4 py-3 mb-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Ask for{" "}
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {trialDurationDays} day{trialDurationDays === 1 ? "" : "s"} of free access
          </span>{" "}
          to this course while your payment is verified — an admin reviews every request.
        </p>
        <Button variant="INFO" onClick={requestTrial} disabled={loading}>
          {loading ? "Requesting..." : "Request Free Trial"}
        </Button>
      </div>
      {error && <p className="text-xs text-red-600 dark:text-red-400 mt-2">{error}</p>}
    </div>
  );
}

function Registration({
  title,
  classRoom,
  onSignIn,
}: {
  title: string;
  classRoom: Classroom;
  onSignIn?: () => void;
}) {
  const user = useAppSelector(getUser);
  const isLoggedIn_ = useAppSelector(isLoggedIn);
  const authChecked = useAppSelector(getAuthChecked);
  const onBoardingStatus = useAppSelector(getOnBoardingStatus);
  const formRef = useRef(null) as React.RefObject<HTMLDivElement | null>;
  // SWR v2's hook returns `mutate`, not `revalidate` (that was the v0.x
  // name) -- destructuring a nonexistent `revalidate` silently produced
  // undefined, and every downstream `refetch()` call (RegisterForm,
  // PaymentMethods, RegisterUserImage, RegisterUploadImage) then crashed
  // with "refetch is not a function" the first time an effect actually
  // invoked it -- the real cause behind the repeated registration-flow
  // crashes. Aliased to keep the existing `refetch={revalidate}` prop
  // wiring below unchanged.
  const { data: registration, mutate: revalidate } = useSWR(
    classRoomRegistration + classRoom._id.$oid,
  );
  const { data: trialInfo } = useSWR(getTrialInfo);
  const dispatch = useAppDispatch();
  useEffect(() => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [registration]);

  // Wait for the real auth check before assuming a logged-out visitor --
  // isLoggedIn's initial value is false, so an already-logged-in user
  // briefly saw "you need to log in" here on every page load.
  if (!authChecked) return null;

  if (!isLoggedIn_ || !user)
    return (
      <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 text-center">
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          You need to be logged in to register for this classroom.
        </p>
        <Button variant="INFO" onClick={() => { onSignIn ? onSignIn() : dispatch(showSignInDrawer()); }}>
          Log in to Register
        </Button>
      </section>
    );
  if (isLoggedIn_ && !onBoardingStatus?.mobileVerified) {
    return (
      <section className="rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-6">
        <p className="text-amber-800 dark:text-amber-300 text-sm font-medium mb-2">
          Mobile verification required
        </p>
        <p className="text-amber-700 dark:text-amber-400 text-sm mb-4">
          Please verify your mobile number before registering for a classroom.
        </p>
        <Link href={"/legacyuser/mobileupdate"}>
          <span className="inline-block px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-colors">
            Verify Mobile Number →
          </span>
        </Link>
      </section>
    );
  }
  if (registration === undefined) {
    return (
      <section className="w-full rounded-2xl border border-gray-200 bg-white p-5 text-sm text-gray-600">
        Loading registration form...
      </section>
    );
  }
  if (!registration) {
    return (
      <section className="w-full rounded-2xl border border-gray-200 bg-white p-5 text-sm text-gray-600">
        Registration form is not available right now.
      </section>
    );
  }
  return (
    <main title={title} ref={formRef} className="min-w-0">
      <TrialNotice
        classroomId={classRoom._id.$oid}
        trialDurationDays={trialInfo?.data?.trialDurationDays}
        trialEnabled={classRoom.trialEnabled}
      />
      <Steps
        currentPage={
          registration.data.registrantDetails == null
            ? 0
            : registration.data.registrantDetails.paymentDetails.mode == null
              ? 1
              : registration.data.registrantDetails?.isVerified &&
                  registration.data.registrantDetails?.userImage
                ? 3
                : 2
        }
        stepper="linear"
        className="mt-8"
        stepBoxStyle="steps bg-white dark:bg-gray-800 border-2 rounded-lg py-4 px-2 sm:px-4 md:py-8 md:px-8 border-gray-300 dark:border-gray-700 overflow-x-hidden"
        stepBoxTitle="Enroll in 3 Steps"
        stepperWrapperStyle="mt-7"
        stepBoxDescription="Fill in your details, pay, and you're in -- each step unlocks the next."
      >
        <RegisterStep
          title="Your Details"
          icon={<DocumentIcon />}
          nextText="Payment"
          showNavigation={!!registration.data.registrantDetails}
        >
          <RegisterForm
            classRoom={classRoom}
            registration={registration.data.registrantDetails}
            refetch={revalidate}
          />
        </RegisterStep>
        <RegisterStep
          title="Payment"
          icon={<WalletIcon />}
          prevText="Your Details"
          nextText={
            registration.data.registrantDetails?.paymentDetails?.mode
              ? "Confirm Payment"
              : ""
          }
        >
          <PaymentMethods
            classRoom={classRoom}
            registration={registration.data.registrantDetails}
            refetch={revalidate}
            paymentDetails={classRoom.paymentInstructions}
          />
        </RegisterStep>

        <RegisterStep
          title="Confirm Payment"
          icon={<ImageIcon />}
          prevText="Payment"
          nextText={
            registration.data.registrantDetails?.userImage
              ? "Receipt"
              : ""
          }
        >
          <RegisterUserImage
            registration={registration.data.registrantDetails}
            refetch={revalidate}
          />
        </RegisterStep>
        <RegisterStep
          title="Receipt"
          icon={<BillIcon />}
          prevText="Confirm Payment"
        >
          <RegisterDownloadReciept
            registration={registration.data.registrantDetails}
            feePaid={
              registration?.data?.registrantDetails?.paymentDetails?.info.amt
            }
            name={classRoom.name}
          />
        </RegisterStep>
      </Steps>
    </main>
  );
}

export default Registration;
